import type { ReactNode } from "react"

import { RecurringRevenueEnumOptions } from "@boundless/bl-api-client/public/models/RecurringRevenueEnumLabels"
import { useFormContext, type FieldPath } from "react-hook-form"

import { FormChipGroup } from "@/components/form-elements/FormChipGroup"
import { FormMoneyInput } from "@/components/form-elements/FormMoneyInput"
import { FormToggleCard } from "@/components/fundready/FormToggleCard"
import { FormField } from "@/components/ui/form"
import { type FundReadyIntakeFormValues } from "@/lib/fundready/schema"

const UCC_FLAGS: FieldPath<FundReadyIntakeFormValues>[] = [
  "arUnderUcc",
  "equipmentUnderUcc",
  "inventoryUnderUcc",
  "realEstateUnderUcc",
]

function WhenAnyChecked({
  names,
  children,
}: {
  names: FieldPath<FundReadyIntakeFormValues>[]
  children: ReactNode
}) {
  const form = useFormContext<FundReadyIntakeFormValues>()
  const [head, ...tail] = names
  if (!head) return null
  return (
    <FormField
      control={form.control}
      name={head}
      render={({ field }) =>
        field.value ? (
          <>{children}</>
        ) : (
          <WhenAnyChecked names={tail}>{children}</WhenAnyChecked>
        )
      }
    />
  )
}

export function StepAssets() {
  return (
    <>
      <div className="flex flex-col gap-3">
        <FormToggleCard<FundReadyIntakeFormValues>
          name="hasAr"
          title="Outstanding B2B invoices"
          description="Money customers owe you (receivables)"
          amountName="arAmount"
          amountPlaceholder="$60,000"
          uccName="arUnderUcc"
          securedDebtName="arSecuredDebt"
          securedDebtLabel="Debt currently secured against your invoices"
          securedDebtInfo="Only include debt secured specifically by your invoices, like factoring or AR financing balances. If a lender's claim covers your whole business rather than just your invoices, enter that total in the blanket lien field below instead."
        />
        <FormToggleCard<FundReadyIntakeFormValues>
          name="hasEquipment"
          title="Owned equipment or vehicles"
          description="Approximate resale value"
          amountName="equipmentAmount"
          amountPlaceholder="$120,000"
          uccName="equipmentUnderUcc"
          securedDebtName="equipmentSecuredDebt"
          securedDebtLabel="Debt currently secured against your equipment"
          securedDebtInfo="Only include debt secured specifically by your equipment, like equipment loans or lease balances. If a lender's claim covers your whole business rather than just your equipment, enter that total in the blanket lien field below instead."
        />
        <FormToggleCard<FundReadyIntakeFormValues>
          name="hasInventory"
          title="Sellable inventory"
          description="At cost, ready to sell"
          amountName="inventoryAmount"
          amountPlaceholder="$80,000"
          uccName="inventoryUnderUcc"
          securedDebtName="inventorySecuredDebt"
          securedDebtLabel="Debt currently secured against your inventory"
          securedDebtInfo="Only include debt secured specifically by your inventory, like inventory or purchase-order financing. If a lender's claim covers your whole business rather than just your inventory, enter that total in the blanket lien field below instead."
        />
        <FormToggleCard<FundReadyIntakeFormValues>
          name="hasRealEstate"
          title="Commercial real estate"
          description="Owned by the business or owner"
          amountName="realEstateAmount"
          amountPlaceholder="$400,000"
          uccName="realEstateUnderUcc"
          securedDebtName="realEstateSecuredDebt"
          securedDebtLabel="Debt currently secured against your real estate"
          securedDebtInfo="Only include debt secured specifically by your property, like a commercial mortgage. If a lender's claim covers your whole business rather than just your property, enter that total in the blanket lien field below instead."
        />
      </div>

      <WhenAnyChecked names={UCC_FLAGS}>
        <FormMoneyInput<FundReadyIntakeFormValues>
          name="uccBalance"
          label="Total outstanding balance under the UCC (blanket lien)"
          info="If any lender has a claim on your business as a whole - not tied to one specific asset - enter the total you owe here. This is common with merchant cash advances, lines of credit, and SBA loans. Don't double-count debt you already entered for a specific asset above."
          placeholder="$60,000"
        />
      </WhenAnyChecked>

      <FormChipGroup<
        FundReadyIntakeFormValues,
        FundReadyIntakeFormValues["recurringRevenue"]
      >
        name="recurringRevenue"
        label="How much of your revenue is recurring?"
        options={RecurringRevenueEnumOptions}
      />
    </>
  )
}
